import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { SiteAuthCheckResponse } from '../models/site-auth-check-response';
import { ColombiaIframeService } from './colombia-iframe.service';
import { tap } from 'rxjs';

/**
 * Response shape of GET /{locale}/services-new/{service_id}/ (ServiceNewPage)
 * when the applicant has a pending task to work on.
 *
 * TOBE-17748: on this shape the endpoint returns exactly these four keys.
 * Everything else about the file - process_history, file_id, file_content,
 * active_task - is nested inside the serialized `options` blob. This interface
 * previously declared top-level `process_history` and `applicant_file`, which
 * let `formData['process_history']` type-check while being undefined at
 * runtime on this shape.
 *
 * It is NOT the only shape a Part A load resolves to. When the file has no
 * pending applicant task - rejected, approved, or waiting on an officer -
 * ServiceNewPage answers with a 302 to /details, which the browser follows
 * transparently, and the observable resolves to a
 * DetailsApplicationStateResponse instead. See PartAApplicationStateLoadResponse.
 *
 * `applicant_file` is kept optional only so the shared
 * initForm(PartA | PartB) union still compiles; Part B does send it.
 */
export interface PartAApplicationStateResponse {
  general_translations?: string;
  options: string;
  service_forms: string;
  service_id: string;
  applicant_file?: any;
}

export interface PartBApplicationStateResponse {
  lang: string;
  service_id: string;
  role_type: string;
  process_id: string;
  is_overview: boolean;
  back_to_service_id: string;
  back_to_role: string;
  service_forms: string;
  service: any;
  service_name: string;
  service_roles: any[]; // NOTE: Duplicated in options JSON - component uses FormOptions.service_roles instead
  general_translations?: string;
  process_info: Record<string, any>;
  process_history: any[];
  services: any[];
  current_service_id: string;
  applicant_file: any;
  role_name: string;
  current_role: Record<string, any>;
  options: string;
}

/**
 * Response from the /details endpoint for read-only file viewing.
 * Used by Inspector view to load files of ANY status (pending, validated, rejected).
 * Reference: eregcms/apps/commons/views.py:693-815 - SingleApplication class
 * TOBE-15450 Issue 9: Correct approach for viewing finished files
 *
 * TOBE-17748: Part A receives this shape too, whenever ServiceNewPage
 * redirects a file with no pending applicant task here. Note that
 * `process_history` is a JSON string at the TOP level and `options` holds
 * only `file_content` and `service` - the opposite of the /services-new/
 * layout. The backend has already dropped officer-only tasks
 * (visibleForApplicant: false) from this history.
 */
export interface DetailsApplicationStateResponse {
  service_forms: string; // JSON string of forms from previewForms mechanism
  options: string; // JSON string containing file_content and service info
  general_translations?: string;
  applicant_file: any;
  process_history: string; // JSON stringified array
  process_id: string;
}

/**
 * What loadPartAApplicationState() actually resolves to: the /services-new/
 * payload when the applicant has a pending task, or the /details payload
 * after the backend's redirect when they do not (TOBE-17748). Consumers must
 * handle both - the two shapes keep `process_history` in different places.
 */
export type PartAApplicationStateLoadResponse =
  | PartAApplicationStateResponse
  | DetailsApplicationStateResponse;

@Injectable({
  providedIn: 'root'
})
export class FormControllerService {
  private readonly baseUrl: string = environment.BASE_API_URL;

  constructor(
    private readonly http: HttpClient,
    private readonly iframeService: ColombiaIframeService
  ) {}

  loadPartAApplicationState(
    locale: string,
    serviceId: string,
    fileId: string | null,
    processId: string | null
  ) {
    let apiRequestParams = new HttpParams();
    if (processId) {
      apiRequestParams = apiRequestParams.append('process_id', processId);
    } else if (fileId) {
      apiRequestParams = apiRequestParams.append('file_id', fileId);
    }

    return this.http.get<PartAApplicationStateLoadResponse>(
      `${environment.BASE_URL}${locale}/services-new/${serviceId}/`,
      {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params: apiRequestParams
      }
    );
  }

  loadPartBApplicationState(
    locale: string,
    serviceId: string,
    roleName: string,
    processId: string
  ) {
    return this.http.get<PartBApplicationStateResponse>(
      `${environment.BASE_URL}${locale}/part-b/edit/${serviceId}/${roleName}/${processId}`,
      {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }
    );
  }

  saveFile(fileId: string, fileData: object, partialSave: boolean = false) {
    let url = `${this.baseUrl}files/${fileId}/data/WIZARD?ignore_formio_validation=1`;
    if (partialSave) {
      return this.http.patch(url, { data_content: JSON.stringify(fileData) });
    } else {
      return this.http.put(url, { data_content: JSON.stringify(fileData) });
    }
  }

  authCheck(locale: string, serviceId: string) {
    let apiRequestParams = new HttpParams();
    apiRequestParams.set('service_id', serviceId);
    apiRequestParams.set('lang', locale);

    return this.http.get<SiteAuthCheckResponse>(
      `${environment.BASE_URL}site-auth-check/`,
      {
        params: apiRequestParams
      }
    );
  }

  startProcess(fileId: string, fileData: object) {
    const url = `${this.baseUrl}files/${fileId}/start_process`;

    return this.http.post(url, { data_content: JSON.stringify(fileData) }).pipe(
      tap(() => {
        if (this.iframeService.isRunningInIframe()) {
          this.iframeService.sendMessageToParent('process_started', {
            fileId,
            timestamp: new Date().toISOString(),
            success: true
          });
        }
      })
    );
  }

  updateProcess(
    processId: string,
    roleName: string,
    formType: string,
    data: object
  ) {
    const url = `${this.baseUrl}process/${processId}/role-forms/${roleName}/${formType}`;

    return this.http.put(url, { data: data }).pipe(
      tap(() => {
        if (this.iframeService.isRunningInIframe()) {
          this.iframeService.sendMessageToParent('process_updated', {
            processId,
            timestamp: new Date().toISOString(),
            success: true
          });
        }
      })
    );
  }

  /**
   * Deletes a process instance
   * Reference: eregcms/assets/js/partb.page.js lines 84-88, 277-287
   * Backend endpoint: DELETE /api/v1/delete-process/{processId}/
   * New token-authenticated API endpoint (eregcms/apps/api/views.py:771, urls.py:33)
   */
  deleteProcess(processId: string) {
    const url = `${environment.BASE_URL}api/v1/delete-process/${processId}/`;
    return this.http.delete(url).pipe(
      tap(() => {
        if (this.iframeService.isRunningInIframe()) {
          this.iframeService.sendMessageToParent('process_deleted', {
            processId,
            timestamp: new Date().toISOString(),
            success: true
          });
        }
      })
    );
  }

  /**
   * Restarts a process instance after a specific role
   * Reference: eregcms/assets/js/partb.page.js lines 341-371
   * Backend endpoint: POST /api/v1/restart-process/{processDefinitionId}/
   * New token-authenticated API endpoint (eregcms/apps/api/views.py:732, urls.py:32)
   * Backend expects payload wrapped in "data" key: {data: {...}}
   */
  restartProcess(
    processDefinitionId: string,
    processInstanceIds: string[],
    activityId: string,
    ended: boolean
  ) {
    const url = `${environment.BASE_URL}api/v1/restart-process/${processDefinitionId}/`;

    const payload = {
      ended: ended,
      instructions: [
        {
          type: 'startAfterActivity',
          activityId: activityId
        }
      ],
      processInstanceIds: processInstanceIds,
      initialVariables: false,
      skipCustomListeners: false,
      withoutBusinessKey: false
    };

    return this.http.post(url, { data: payload }).pipe(
      tap(() => {
        if (this.iframeService.isRunningInIframe()) {
          this.iframeService.sendMessageToParent('process_restarted', {
            processDefinitionId,
            activityId,
            timestamp: new Date().toISOString(),
            success: true
          });
        }
      })
    );
  }

  /**
   * Loads application state for read-only viewing via the /details endpoint.
   * This is the correct approach for Inspector view - works for ANY file status.
   * Reference: eregcms/apps/commons/views.py:693-815 - SingleApplication class
   * TOBE-15450 Issue 9: Enables viewing validated/rejected/pending files in Inspector
   *
   * @param locale - Language code (e.g., 'en', 'fr')
   * @param processId - The process instance ID
   * @param tab - Optional tab parameter (e.g., 'inspector' for Inspector view)
   */
  loadDetailsApplicationState(locale: string, processId: string, tab?: string) {
    let params = new HttpParams().set('process_id', processId);
    if (tab) {
      params = params.set('tab', tab);
    }
    return this.http.get<DetailsApplicationStateResponse>(
      `${environment.BASE_URL}${locale}/details`,
      {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params
      }
    );
  }

  /**
   * Cancels a file submission.
   * Reference: eregcms/assets/js/backend.service.js:198-203
   */
  cancelFile(fileId: string) {
    const url = `${this.baseUrl}files/${fileId}/cancel_file`;
    return this.http.delete(url);
  }

  /**
   * Updates a specific form type (e.g., A6) for a file.
   * Reference: eregcms/assets/js/backend.provider.js:31-37
   */
  updateFile(
    fileId: string,
    formType: string,
    data: object,
    ignoreFormioValidation: boolean = false
  ) {
    let url = `${this.baseUrl}files/${fileId}/data/${formType}`;
    if (ignoreFormioValidation) {
      url += '?ignore_formio_validation=1';
    }
    return this.http.put(url, { data_content: JSON.stringify(data) });
  }

  /**
   * Updates a specific form type for a process role (used when no file_id is available).
   * Reference: eregcms/assets/js/applicationservice.controller.js:218-226
   */
  updateProcessRoleForm(
    processId: string,
    roleName: string,
    formType: string,
    data: object
  ) {
    const url = `${this.baseUrl}process/${processId}/role-forms/${roleName}/${formType}`;
    return this.http.patch(url, { data: data });
  }

  /**
   * Generates a certificate via backend.
   * Reference: eregcms/assets/js/backend.service.js:224-233
   */
  generateCertificate(path: string, data: object) {
    const url = `${this.baseUrl}process/generate-cert`;
    return this.http.post(url, { path, data });
  }
}
